import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useState } from "react";
import { FormattedMessage } from "react-intl";
import LazyLoad from "react-lazyload";
import { toast } from "react-toastify";
import { useUser } from "../../contexts/UserContext";
import { usePostFunctions } from "../../hooks/posts/usePostFunctions";
import postService from "../../services/post-service";
import PostContent, { PostComponentProps } from "../../types/post-type";
import { useEditHandler } from "../../hooks/useEditHandler";
import dayjs from "dayjs";
import lscache from "lscache";

const PostComponent: React.FC<PostComponentProps> = ({
  canDelete,
  showCreateButton,
  isPublic,
}) => {
  const initialFormData = {
    title: "",
    content: "",
    date: dayjs(),
  };
  const [formData, setFormData] = useState<PostContent>(initialFormData);

  const { userId } = useUser();

  const {
    privatePosts,
    publicPosts,
    visibility,
    isLoading,
    fetchPosts,
    setPublicPosts,
    setPrivatePosts,
    setVisibility,
    handlePostDelete,
  } = usePostFunctions();

  const { handleEditClick, editing, setEditing, showForm, setShowForm } =
    useEditHandler<PostContent>(postService);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (value === null) {
      return;
    }
    setFormData({ ...formData, [name]: value });
    lscache.set("post-" + name, value, 30);
  };

  const handleEditPost = async (postData: PostContent) => {
    const data = await handleEditClick(postData, visibility);
    if (data) {
      setFormData(data);
    }
  };

  const handlePostSubmit = async () => {
    try {
      const postContent: PostContent = {
        id: formData.id,
        title: formData.title,
        content: formData.content,
        date: formData.date,
        creatorId: userId,
        visibility: visibility,
      };
      if (editing === true) {
        await postService.updatePost(
          postContent.id!,
          visibility,
          postContent
        );
        toast.success("Apartment updated successfully!", {
          position: "bottom-right",
        });
      } else {
        await postService.createPost(postContent);
        toast.success("Success!", {
          position: "bottom-right",
        });
      }
      setEditing(false);
      setFormData(initialFormData);
      setShowForm(false);
      if (visibility === "public") {
        setPublicPosts((prevPosts) => [postContent, ...prevPosts]);
      } else if (userId) {
        setPrivatePosts((prevPosts) => [postContent, ...prevPosts]);
      }
      fetchPosts();
    } catch (error) {
      console.error(error);
      toast.error("Error publishing post", {
        position: "bottom-right",
      });
    }
  };

  const sortedPrivatePosts = [...privatePosts].sort((a, b) =>
    dayjs(a.date).isBefore(dayjs(b.date)) ? 1 : -1
  );
  const sortedPublicPosts = [...publicPosts].sort((a, b) =>
    dayjs(a.date).isBefore(dayjs(b.date)) ? 1 : -1
  );

  return (
    <Container>
      <Grid container>
        <Grid item xs={12} sx={{ my: 2 }}>
          <Typography variant="h2">
            <FormattedMessage id="posts" />
          </Typography>
        </Grid>
        {showCreateButton && (
          <Grid item xs={12} md={4}>
            <Button
              sx={{ mt: 2, mb: 2, width: { xs: "100%", sm: "90%" } }}
              variant="contained"
              onClick={() => setShowForm(!showForm)}
            >
              {showForm === null && (
                <Typography variant="body1">
                  <FormattedMessage id="create-post" />
                </Typography>
              )}
              {showForm !== null && (
                <Typography variant="body1">
                  {showForm ? (
                    <FormattedMessage id="hide" />
                  ) : (
                    <FormattedMessage id="show" />
                  )}
                </Typography>
              )}
            </Button>
          </Grid>
        )}
        <Grid item xs={12}>
          {isLoading && <CircularProgress />}
          {showForm && (
            <Box sx={{ my: 2 }}>
              <Typography variant="h3">
                <FormattedMessage id="create-post" />
              </Typography>

              <TextField
                fullWidth
                name="title"
                margin="normal"
                variant="outlined"
                label={<FormattedMessage id="title" />}
                value={formData.title}
                onChange={handleChange}
              />

              <TextField
                fullWidth
                name="content"
                margin="normal"
                variant="outlined"
                label={<FormattedMessage id="content" />}
                value={formData.content}
                onChange={handleChange}
                multiline
                rows={4}
              />

              <FormControl component="fieldset" sx={{ mt: 2 }}>
                <FormLabel component="legend">Visibility</FormLabel>
                <RadioGroup
                  row
                  value={visibility}
                  onChange={(e) =>
                    setVisibility(e.target.value as "public" | "private")
                  }
                >
                  <FormControlLabel
                    value="public"
                    control={<Radio />}
                    label="Public"
                  />
                  <FormControlLabel
                    value="private"
                    control={<Radio />}
                    label="Private"
                  />
                </RadioGroup>
              </FormControl>

              <Button
                variant="contained"
                color="primary"
                onClick={handlePostSubmit}
              >
                <FormattedMessage id="submit" />
              </Button>
            </Box>
          )}
        </Grid>
        {isPublic ? (
          <>
            {sortedPublicPosts.map((post: PostContent) => (
              <LazyLoad key={post.id} style={{ width: "100%" }}>
                <Grid item xs={12} p={2}>
                  <Card sx={{ my: 2, p: 2 }}>
                    <Typography variant="body2">
                      {dayjs(post.date).format("MMMM D, YYYY")}
                    </Typography>
                    <Typography variant="h3">{post.title}</Typography>
                    <Typography variant="body1">{post.content}</Typography>
                    {(canDelete || post.creatorId === userId) && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                          onClick={() => handlePostDelete(post)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                    {post.creatorId === userId && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                          onClick={() => handleEditPost(post)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Card>
                </Grid>
              </LazyLoad>
            ))}
          </>
        ) : (
          <>
            {sortedPrivatePosts.map((post: PostContent) => (
              <LazyLoad key={post.id} style={{ width: "100%" }}>
                <Grid item xs={12} p={2}>
                  <Card sx={{ my: 2, p: 2 }}>
                    <Typography variant="body2">
                      {dayjs(post.date).format("MMMM D, YYYY")}
                    </Typography>
                    <Typography variant="h3">{post.title}</Typography>
                    <Typography variant="body1">{post.content}</Typography>
                    {(canDelete || post.creatorId === userId) && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                          onClick={() => handlePostDelete(post)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                    {post.creatorId === userId && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                          onClick={() => handleEditPost(post)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Card>
                </Grid>
              </LazyLoad>
            ))}
          </>
        )}
      </Grid>
    </Container>
  );
};

export default PostComponent;
